import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { llmTrackerCampaignsQueryClient } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/llmTrackerCampaignsQueryClient";
import type {
    IAiTrackerCampaign,
    ICampaignConfig,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { CampaignStatus, ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { DefaultFetchService, NoCacheHeaders } from "app/services/fetchService";

interface ICampaignStatusResponse {
    status: CampaignStatus;
}

export const useReportStatus = (campaign: IAiTrackerCampaign, campaignConfig: ICampaignConfig): ReportStatus => {
    const [isPending, setPending] = useState<boolean>(false);
    const [campaignsRefetched, setCampaignsRefetched] = useState<boolean>(false);
    const configStatus = campaignConfig?.status;
    const shouldCheckStatus = !!campaign && configStatus === CampaignStatus.Pending;
    const statusFetchParams = {
        campaignGuid: campaign?.id,
        configId: campaignConfig?.configId?.split(",")?.[0],
    };

    const { data: statusResponse } = useQuery({
        enabled: shouldCheckStatus && Object.values(statusFetchParams).every(Boolean),
        queryKey: ["campaignReportStatus", ...Object.values(statusFetchParams)],
        queryFn: () => {
            const fetchService = DefaultFetchService.getInstance();
            return fetchService.get<ICampaignStatusResponse>("/api/aiTracker/campaign/status", statusFetchParams, {
                headers: NoCacheHeaders, // we are disabling browser caching, to make sure react-query will handle the caching.
            });
        },
        refetchInterval: (query) => {
            const campaignIsNotReady = query.state.data?.status === CampaignStatus.Pending;
            if (!isPending && campaignIsNotReady) {
                setPending(true);
            }

            return campaignIsNotReady && 15_000;
        },
        refetchOnWindowFocus: false,
        retry: false,
        gcTime: 0, // Disable React Query caching - data will be garbage collected immediately
        staleTime: 0, // Data is considered stale immediately, forcing fresh fetches
        throwOnError: (error) => {
            Sentry.errorTracker({
                e: error,
                message: "Error fetching report status",
                level: "error",
                params: { extra: { ...statusFetchParams } },
            });

            return false;
        },
    });

    useEffect(() => {
        const refetchCampaigns = async () => {
            await llmTrackerCampaignsQueryClient.refreshCampaigns();
            setCampaignsRefetched(true);
        };

        if (!campaignsRefetched && isPending && statusResponse?.status === CampaignStatus.Completed) {
            void refetchCampaigns();
        }
    }, [isPending, statusResponse?.status]);

    const isFailedConfig = !shouldCheckStatus && campaign && configStatus === CampaignStatus.Failed;
    if (statusResponse?.status === CampaignStatus.Failed || isFailedConfig) {
        return ReportStatus.Error;
    }

    if (shouldCheckStatus && (statusResponse?.status !== CampaignStatus.Completed || !campaignsRefetched)) {
        return ReportStatus.NotReady;
    }

    return ReportStatus.Ready;
};
